import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { ConversationFileUpload } from '../../../../core/models/fileUpload/conversationFileUpload';
import { PersonalAssistantUpdate } from '../../../../features/assistantform/pages/PersonalAssistantUpdatePage';
import { AssistantDetailModel } from '../../../../features/marketplace/types';
import { assistantKeys } from '../../../../features/marketplace/services/queries/assistantApiQueries';
import { assistantAPI } from '../../../../features/marketplace/services/api/assistantAPI';
import { FunctionalAssistantUpdate } from '../../../../features/assistantform/pages/FunctionalAssistantUpdatePage';

export const Route = createFileRoute('/_app/assistant/update/$assistantName')({
  component: RouteComponent,
  gcTime: 0,
  shouldReload: true,
  loader: async (ctx) => {
    const { assistantName } = ctx.params;
    const assistants = await ctx.context.queryClient.ensureQueryData({
      queryKey: assistantKeys.lists(),
      queryFn: () => assistantAPI.listAssistantsAsync(),
    });

    const assistantDetails = assistants.find((assistant) => assistant.name === assistantName);
    const assistantLogo = assistantDetails
      ? await ctx.context.queryClient
          .ensureQueryData({
            queryKey: assistantKeys.logo(assistantDetails.name),
            queryFn: () => assistantAPI.getAssistantLogo(assistantDetails.name),
          })
          .catch(() => null)
      : null;

    return { assistantLogo, assistants };
  },
});

function RouteComponent() {
  const { assistantName } = Route.useParams();
  const { assistantLogo, assistants } = useLoaderData({
    from: '/_app/assistant/update/$assistantName',
  });

  const assistantDetails = assistants.find((a) => a.name === assistantName);
  const conversationId = assistantDetails?.files?.[0]?.conversation_id;
  const files: ConversationFileUpload[] = assistantDetails?.files
    ? assistantDetails?.files?.map((file) => ({
        file: file.file_name,
        uploadId: file.upload_id,
        conversationId: conversationId,
        isUploaded: true,
      }))
    : [];

  function blobToFile(blob: Blob, fileName: string): File {
    // Determine file extension from MIME type
    const mimeToExt: Record<string, string> = {
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/svg+xml': '.svg',
      'image/webp': '.webp',
    };
    const ext = mimeToExt[blob.type] || '.png';
    const fileNameWithExt = fileName.includes('.')
      ? fileName
      : `${fileName}${ext}`;

    return new File([blob], fileNameWithExt, {
      type: blob.type,
      lastModified: Date.now(),
    });
  }

  const logo = assistantLogo
    ? blobToFile(assistantLogo, assistantDetails?.standardName ?? 'logo')
    : new File([], 'logo.png', { type: 'image/png' });

  return assistantDetails?.usage === 'Functional' ? (
    <FunctionalAssistantUpdate
      assistantDetails={assistantDetails as AssistantDetailModel}
      assistantLogo={logo}
      conversationId={conversationId}
      files={files}
    />
  ) : (
    <PersonalAssistantUpdate
      assistantDetails={assistantDetails as AssistantDetailModel}
      assistantLogo={logo}
      conversationId={conversationId}
      files={files}
    />
  );
}
